import { useId, type TextareaHTMLAttributes } from 'react';

import './campo.css';

export type PropsCampoLongo = {
  rotulo: string;
  /** Texto de apoio permanente, ligado por `aria-describedby`. */
  dica?: string;
  /** Presente, marca `aria-invalid` e anuncia o texto. */
  erro?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * O `Campo` de várias linhas: mesmo rótulo, mesma dica, mesmo erro, mas um
 * `<textarea>` de verdade no lugar do `<input>`.
 *
 * **Por que existe.** O design system só tinha campo de uma linha, e o plano
 * desta etapa decidiu que o ditado é o do teclado do iOS, não a Web Speech
 * API — "um `<textarea>` comum já tem o microfone do teclado no iPhone. Não
 * há código a escrever, só a garantia de que o campo é um `textarea` de
 * verdade". Num `<input>` a anotação ditada vira uma linha só, sem quebras, e
 * a tecla de retorno fecha o teclado em vez de abrir parágrafo. Como nenhum
 * `<textarea>` cru pode ficar fora de `src/ds/` (a regra de lint da Task 9),
 * o campo longo nasce aqui, não dentro da tela que primeiro precisou dele.
 *
 * A folha de estilo é a mesma do `Campo` (`campo.css`): rótulo, dica e erro
 * são o mesmo desenho, e duas folhas para o mesmo componente se desencontram
 * na primeira correção.
 */
export function CampoLongo({
  rotulo,
  dica,
  erro,
  id,
  className,
  // Uma linha por padrão, e não as duas do estilo do navegador: colapsado, o
  // campo tem de ter a mesma altura do `Campo` de uma linha — é ele que
  // ocupa o lugar do campo curto numa tela onde cada pixel a mais empurra
  // para cima o que estava à mão. Cresce com `rows` quem quiser espaço.
  rows = 1,
  ...resto
}: PropsCampoLongo) {
  // `useId` dá um identificador estável por instância, como no `Campo`.
  const gerado = useId();
  const idCampo = id ?? `campo-longo${gerado}`;
  const idDica = `${idCampo}-dica`;
  const idErro = `${idCampo}-erro`;
  const descritores = [dica ? idDica : null, erro ? idErro : null].filter(
    (x): x is string => x !== null,
  );
  return (
    <div className={className ? `ds-campo ${className}` : 'ds-campo'}>
      <label className="ds-campo__rotulo" htmlFor={idCampo}>
        {rotulo}
      </label>
      {dica ? (
        <p className="ds-campo__dica" id={idDica}>
          {dica}
        </p>
      ) : null}
      <textarea
        className="ds-campo__entrada"
        id={idCampo}
        rows={rows}
        aria-describedby={
          descritores.length > 0 ? descritores.join(' ') : undefined
        }
        aria-invalid={erro ? true : undefined}
        {...resto}
      />
      {erro ? (
        <p className="ds-campo__erro" id={idErro} role="alert">
          <span aria-hidden="true">⚠</span> {erro}
        </p>
      ) : null}
    </div>
  );
}
